import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { Loader2Icon } from 'lucide-react'
import { planSeed } from 'shared/plan-seed'
import { Button } from 'ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'ui/card'
import { Progress } from 'ui/progress'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import { LinkButton } from '~/components/link-button'
import { config } from '~/config/env'
import { MobileBottomLayout } from '~/layout/mobile-bottom'
import { useAccountStore } from '~/stores/account'

export default function SubscriptionUsage() {
  const { t } = useTranslation()
  const planId = useAccountStore(state => state.planId)
  const [isChangePaymentMethodLoading, setIsChangePaymentMethodLoading] = useState(false)
  const remainingCoins = useAccountStore(state => state.coins)

  const navigate = useNavigate()

  if (!planId) return null

  const isFreePlan = planId === config.VITE_FREE_PLAN_ID

  const plan = planSeed.find(p => p.id === planId)
  if (!plan) return null

  const renderContent = () => {
    const coinsUsed = plan.coins - remainingCoins
    const usagePercentage = (coinsUsed / plan.coins) * 100

    return (
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>{t('Coins usage')}</span>
          <span>
            {/* eslint-disable-next-line react/jsx-no-literals */}
            {Math.min(coinsUsed, plan.coins)} / {plan.coins}
          </span>
        </div>
        <Progress className="w-full" value={usagePercentage} />
      </div>
    )
  }

  const changePaymentMethodClick = async () => {
    setIsChangePaymentMethodLoading(true)
    try {
      const { data } = await apiClient.POST('/subscription/change-payment-method')

      if (!data?.clientSecret) {
        toast.error(t('Something went wrong'))

        return
      }

      void navigate({ to: '/settings/subscription/change-payment-method', search: { clientSecret: data.clientSecret } })
    } catch (error) {
      toast.error(t('Network error'))
      console.error({ message: 'Network error', error })
      return
    } finally {
      setIsChangePaymentMethodLoading(false)
    }
  }

  return (
    <MobileBottomLayout>
      <h2 className="text-center text-2xl font-bold">{t('Subscription & usage')}</h2>
      <Card className="mx-auto w-full max-w-2xl">
        <CardHeader>
          <CardTitle>{t(plan.name)}</CardTitle>
          <CardDescription>{t('coins per month for', { coins: plan.coins, price: plan.price })}</CardDescription>
        </CardHeader>
        <CardContent>{renderContent()}</CardContent>
      </Card>
      <LinkButton to="/settings/subscription/change">{t('Change subscription')}</LinkButton>
      {!isFreePlan && (
        <Button disabled={isChangePaymentMethodLoading} onClick={changePaymentMethodClick}>
          {isChangePaymentMethodLoading && <Loader2Icon className="mr-2 size-4 animate-spin" />}
          {t('Change payment method')}
        </Button>
      )}
      <LinkButton to="/settings" variant="outline">
        {t('Back')}
      </LinkButton>
    </MobileBottomLayout>
  )
}
